import { useState } from "react"
import CreatableReactSelect from "react-select/creatable"
import type { GroupBase, StylesConfig } from "react-select"
import type { SelectOption } from "@/components/ui/select"

// Single-select with inline create. Mirrors the dark-surface tokens of
// the standard <Select> wrapper so it drops into the same layouts.
//
// Caller supplies `onCreate(name)`: an async function that POSTs to the
// server and returns the freshly-created option. We block the input
// while the create is in flight so the operator can't double-submit.
//
// Usage:
//   <CreatableSelect<number>
//     value={opts.find(o => o.value === id) ?? null}
//     options={opts}
//     onChange={(o) => o && setId(o.value)}
//     onCreate={async (name) => {
//       const res = await fetch("/ticket_queues", { method: "POST", … })
//       return { value: data.id, label: data.name }
//     }}
//     placeholder="Pick a queue or type a new name…"
//   />

type Props<V extends string | number = string | number> = {
  value:    SelectOption<V> | null
  options:  SelectOption<V>[]
  onChange: (next: SelectOption<V> | null) => void
  onCreate: (name: string) => Promise<SelectOption<V> | null>
  placeholder?: string
  isDisabled?: boolean
  formatCreateLabel?: (input: string) => string
}

const styles: StylesConfig<SelectOption<string | number>, false, GroupBase<SelectOption<string | number>>> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    borderColor: state.isFocused ? "var(--brand-red)" : "var(--border-soft, #2a2a30)",
    borderRadius: 8,
    boxShadow: state.isFocused ? "0 0 0 1px var(--brand-red)" : "none",
    cursor: "pointer",
    transition: "border-color 120ms ease",
    ":hover": { borderColor: "var(--border-strong, #3a3a42)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  singleValue: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)", fontSize: 14, fontWeight: 500 }),
  input: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-subtle, #6b6b75)" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "var(--text-app, #e6e6ea)" : "var(--text-muted, #9a9aa3)",
    padding: "0 8px",
  }),
  clearIndicator: (base) => ({ ...base, color: "var(--text-muted, #9a9aa3)" }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    border: "1px solid var(--border-soft, #2a2a30)",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    marginTop: 4,
    zIndex: 30,
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(224, 69, 91, 0.18)"
      : state.isFocused
        ? "rgba(255,255,255,0.05)"
        : "transparent",
    color: "var(--text-app, #e6e6ea)",
    fontSize: 14,
    borderRadius: 6,
    padding: "8px 10px",
    cursor: "pointer",
  }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--text-muted, #9a9aa3)", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

export function CreatableSelect<V extends string | number = string | number>({
  value, options, onChange, onCreate, placeholder, isDisabled, formatCreateLabel,
}: Props<V>) {
  const [pending, setPending] = useState(false)

  return (
    <CreatableReactSelect<SelectOption<V>, false, GroupBase<SelectOption<V>>>
      value={value}
      options={options}
      onChange={(next) => onChange(next ?? null)}
      onCreateOption={async (input) => {
        const trimmed = input.trim()
        if (!trimmed || pending) return
        setPending(true)
        try {
          const created = await onCreate(trimmed)
          if (created) onChange(created)
        } finally {
          setPending(false)
        }
      }}
      isDisabled={isDisabled || pending}
      isLoading={pending}
      placeholder={placeholder ?? "Pick or type to create…"}
      formatCreateLabel={formatCreateLabel ?? ((input) => `Create "${input}"`)}
      classNamePrefix="rs"
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      styles={styles as unknown as StylesConfig<SelectOption<V>, false, GroupBase<SelectOption<V>>>}
    />
  )
}
